import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import validationSchema from 'src/utils/validationSchema';

import { useTranslate } from 'src/locales';
import { useChannelContext } from 'src/pages/channel/hooks';
import { useGetAssigneeList } from 'src/pages/tickets/hooks/useGetAssigneeList';

export const DID_TYPE_OPTIONS = [
  { value: 'Local-011', label: 'Local - 011' },
  { value: 'Unified Communications-9200', label: 'Unified Communications - 9200' },
  { value: 'Toll free-800', label: 'Toll free - 800' },
];

export function useVoiceStep2() {
  const { t } = useTranslate('translation');
  const { state: channelState, setField, isLoading, onChangeStep } = useChannelContext();
  const { voiceCallDetails } = channelState;

  const schema = validationSchema(
    [
      { name: 'didType', required: true },
      { name: 'assignUsers', required: true },
    ],
    t
  );

  type FormValues = {
    assignUsers: any[];
    didType: string;
  };

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { assignUsers: [], didType: '' },
    values: {
      assignUsers: voiceCallDetails?.assignUsers || [],
      didType: voiceCallDetails?.didType || '',
    },
  });

  const { handleSubmit, watch, setValue, formState } = methods;

  const assigneeList = useGetAssigneeList({
    setValue: setValue as any,
    multiFieldAssign: voiceCallDetails?.assignUsers || [],
  });

  const [assignedId, didType] = watch(['assignUsers', 'didType']);

  const onSubmit = handleSubmit((data) => {
    setField('voiceCallDetails', {
      ...voiceCallDetails,
      assignUsers: data.assignUsers,
      didType: data.didType,
    });
    onChangeStep('next');
  });

  const handleChange = ({ value, fieldName }: { value: any; fieldName: string }) => {
    if (fieldName === 'assignedId') {
      setField('voiceCallDetails', {
        ...voiceCallDetails,
        [fieldName]: value,
        assignedType: value?.isUser ? 'User' : 'Team',
      });
    } else if (fieldName === 'didType') {
      setField('voiceCallDetails', {
        ...voiceCallDetails,
        [fieldName]: value,
      });
    }
  };

  const isNextDisabled = !didType || !assignedId?.length || !formState.isValid;

  return {
    methods,
    assigneeList,
    assignedId,
    didType,
    isLoading,
    isNextDisabled,
    setValue,
    onSubmit,
    handleChange,
  };
}
